import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';

// Import icons
import CodeIcon from '@mui/icons-material/Code';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExploreIcon from '@mui/icons-material/Explore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import AppIcon from '@mui/icons-material/Apps';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ApiIcon from '@mui/icons-material/Api';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import StorageIcon from '@mui/icons-material/Storage';
import BugReportIcon from '@mui/icons-material/BugReport';
import SchemaIcon from '@mui/icons-material/Schema';

// Import the necessary tool components
import TokenDecoder from './TokenDecoder';
import GraphExplorer from './GraphExplorer';
import ManifestViewer from './ManifestViewer';
import ODataQueryBuilder from './ODataQueryBuilder';
import SamlTester from './SamlTester';
import AuthFlowTester from './AuthFlowTester';
import AppOverview from './AppOverview';
import { useMsal } from '@azure/msal-react';
import { Application } from "@microsoft/microsoft-graph-types";
import { Client } from "@microsoft/microsoft-graph-client";
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import TokenService from '../utils/tokenService';
import TokenScopeRequester from './TokenScopeRequester';
import RawOAuthTester from './RawOAuthTester';
import MsalTraceViewer from './MsalTraceViewer';
import OidcMetadataInspector from './OidcMetadataInspector';
import EntraEndpointsExplorer from './EntraEndpointsExplorer';
import ConfidentialClientAuthDebugger from './ConfidentialClientAuthDebugger';
import TokenComparisonTool from './TokenComparisonTool';
import MsalCacheInspector from './MsalCacheInspector';
import MsalErrorDecoder from './MsalErrorDecoder';
import SchemaExtensionsTool from './SchemaExtensionsTool';

// Import ExtendedApplication interface from AuthFlowTester
interface ExtendedApplication {
  appOwnerTenantId?: string;
  signInAudience?: string | null | undefined;
  publisherDomain?: string;
  identifierUris?: string[];
}

interface ToolsContentProps {
  searchQuery: string;
  onToolSelect?: (toolId: string) => void;
  onToolStateChange?: (isToolActive: boolean) => void;
}

type ToolComponentType = 
  | typeof TokenDecoder
  | typeof GraphExplorer
  | typeof ManifestViewer
  | typeof ODataQueryBuilder
  | typeof SamlTester
  | typeof AuthFlowTester
  | typeof EntraEndpointsExplorer
  | typeof SchemaExtensionsTool;

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  component?: ToolComponentType;
  requiresToken?: boolean;
  isNew?: boolean;
  requiredPermissions?: string[];
}

const ToolsContent: React.FC<ToolsContentProps> = ({ searchQuery, onToolSelect, onToolStateChange }) => {
  const { instance, accounts } = useMsal();
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string>("");
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [serviceProviders, setServiceProviders] = useState<Record<string, string>>({});
  
  // State to track if we need to show app selection view
  const [showAppTesting, setShowAppTesting] = useState<boolean>(false);

  const [authInProgress, setAuthInProgress] = useState(false);
  const [authTool, setAuthTool] = useState<string | null>(null);

  // OPTIMIZED: Add state for applications loaded flag
  const [applicationsLoaded, setApplicationsLoaded] = useState<boolean>(false);

  // View mode toggle state with persistence (cards vs list)
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('toolsViewMode') : null;
    return saved === 'list' ? 'list' : 'cards';
  });

  useEffect(() => {
    try { localStorage.setItem('toolsViewMode', viewMode); } catch {}
  }, [viewMode]);

  const handleViewModeChange = (_e: React.MouseEvent<HTMLElement>, next: 'cards' | 'list' | null) => {
    if (next) setViewMode(next);
  };

  // Create a TokenService instance
  const tokenService = React.useMemo(() => {
    return TokenService.getInstance(instance);
  }, [instance]);

    const restoringFromHistory = useRef(false);
  const previousPushedView = useRef<string | null>(null);

  // Helper to compute current logical view
  const currentView = (() => {
    if (activeTool === 'saml') return 'samlTester';
    if (activeTool === 'auth-flow') return 'authFlowTester';
    if (activeTool === 'token-scope-requester') return 'tokenScopeRequester';
    if (activeTool === 'manifest-viewer') return 'manifestViewer';
    if (activeTool === 'confidential-client-auth-tester') return 'confidentialClientAuthTester';
    if (activeTool === 'graph-explorer') return 'graphExplorer';
    if (activeTool === 'odata-builder') return 'odataBuilder';
    if (activeTool === 'entra-endpoints-explorer') return 'entraEndpointsExplorer';
    if (activeTool === 'jwt-decoder') return 'jwtDecoder';
    if (activeTool === 'msal-trace-viewer') return 'msalTraceViewer';
    if (activeTool === 'oidc-metadata-inspector') return 'oidcMetadataInspector';
  if (activeTool === 'raw-oauth-tester') return 'rawOAuthTester';
  if (activeTool === 'token-comparison') return 'tokenComparison';
  if (activeTool === 'msal-cache-inspector') return 'msalCacheInspector';
  if (activeTool === 'msal-error-decoder') return 'msalErrorDecoder';
  if (activeTool === 'schema-extensions') return 'schemaExtensions';
    if (showAppTesting) return 'appTestingOverview';
    return 'toolsList';
  })();

  const pushViewState = (view: string, extra?: Record<string, any>) => {
    if (restoringFromHistory.current) return;
    if (previousPushedView.current === view) return;
    previousPushedView.current = view;
    const state: any = { appView: view, ...extra };
    window.history.pushState(state, '');
  };

  // Initialize base history entry once
  useEffect(() => {
    window.history.replaceState({ appView: 'toolsList' }, '');
  }, []);

  // React to browser Back / Forward
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      restoringFromHistory.current = true;
      const s = (e.state || {}) as { appView?: string; appId?: string };

      // Reset common state
      setActiveTool(null);
      setSelectedApp(null);
      setShowAppTesting(false);

      switch (s.appView) {

        // Views that depend on a selected app
        case 'samlTester': {
          if (s.appId) {
            const found = applications.find(a => a.id === s.appId || a.appId === s.appId) || null;
            if (found) setSelectedApp(found);
          }
          setActiveTool('saml');
          break;
        }

        case 'authFlowTester': {
          if (s.appId) {
            const found = applications.find(a => a.id === s.appId || a.appId === s.appId) || null;
            if (found) setSelectedApp(found);
          }
          setActiveTool('auth-flow');
          break;
        }

        // Views that do NOT require a selected app
        case 'manifestViewer':
          setActiveTool('manifest-viewer');
          break;

        case 'tokenScopeRequester':
          setActiveTool('token-scope-requester');
          break;

        case 'graphExplorer':
          setActiveTool('graph-explorer');
          break;

        case 'odataBuilder':
          setActiveTool('odata-builder');
          break;

        case 'entraEndpointsExplorer':
          setActiveTool('entra-endpoints-explorer');
          break;

        case 'jwtDecoder':
          setActiveTool('jwt-decoder');
          break;

        case 'rawOAuthTester':
          setActiveTool('raw-oauth-tester');
          break;

        case 'msalTraceViewer':
          setActiveTool('msal-trace-viewer');
          break;

        case 'oidcMetadataInspector':
          setActiveTool('oidc-metadata-inspector');
          break;

        case 'confidentialClientAuthTester':
          setActiveTool('confidential-client-auth-tester');
          break;

        case 'tokenComparison':
          setActiveTool('token-comparison');
          break;

        case 'msalCacheInspector':
          setActiveTool('msal-cache-inspector');
          break;

        case 'msalErrorDecoder':
          setActiveTool('msal-error-decoder');
          break;

        case 'schemaExtensions':
          setActiveTool('schema-extensions');
          break;

        case 'toolsList':
        default:
          if (onToolStateChange) onToolStateChange(false);
          break;
      }

      // Optional: if you want parent to know a tool is active after a popstate
      if (s.appView && s.appView !== 'toolsList') {
        if (onToolStateChange) onToolStateChange(true);
      }

      setTimeout(() => {
        restoringFromHistory.current = false;
        previousPushedView.current = s.appView || null;
      }, 0);
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [applications, onToolStateChange]);

  // Push history when logical view changes (forward navigation)
  useEffect(() => {
    if (currentView === 'toolsList') return; // Do not push base repeatedly
    const extra: Record<string, any> = {};
    if (selectedApp?.id || selectedApp?.appId) {
      extra.appId = selectedApp.id || selectedApp.appId;
    }
    pushViewState(currentView, extra);
  }, [currentView, selectedApp]);

  useEffect(() => {
    // This will run when the ToolsContent component is mounted/remounted
    const lastToolActivation = sessionStorage.getItem('toolActivationTime');
    const currentNavigation = new Date().toISOString();
    
    // If there's a significant time gap (user navigated away and came back)
    // or this is a fresh navigation, reset the tool state
    if (!lastToolActivation || 
        new Date(currentNavigation).getTime() - new Date(lastToolActivation).getTime() > 1000) {
      console.log('Navigation detected, resetting tools state');
      setActiveTool(null);
      setAccessToken("");
      setSelectedApp(null);
      setSelectedTool(null);
      setShowAppTesting(false);
      sessionStorage.removeItem('lastActiveTool');
    }
    
    // Set cleanup function to update the last active tool when unmounting
    return () => {
      if (activeTool) {
        sessionStorage.setItem('lastActiveTool', activeTool);
      }
    };
  }, []);
  
  // OPTIMIZED: Load applications with proper caching and performance improvements
  useEffect(() => {
    // Only load if not already loaded and we have accounts
    if (!accounts.length || applicationsLoaded) return;
    
    const loadApplicationsOptimized = async () => {
      setLoading(true);
      try {
        const allApplications: Application[] = [];
        
        // Get token for Graph API
        let accessToken: string;
        try {
          // Prefer least-privilege read scope
          accessToken = await tokenService.getToken(["Application.Read.All"], accounts[0]);
        } catch (e: any) {
          // If consent is required, prompt interactively and retry once
          if (e instanceof InteractionRequiredAuthError || e?.errorCode === 'consent_required') {
            console.log("🔧 Tools: Consent required for Application.Read.All, invoking popup...");
            const resp = await instance.acquireTokenPopup({
              scopes: ["Application.Read.All"],
              account: accounts[0]
            });
            accessToken = resp.accessToken;
          } else {
            throw e;
          }
        }
        
        // Initialize Graph client
        const client = Client.init({ authProvider: (done) => done(null, accessToken) });
        
        // PRODUCTION: Dynamic pagination with proper error handling
        let nextLink: string | undefined;
        let pageCount = 0;
        const MAX_PAGES = 100; // Safety limit for any tenant
        let consecutiveEmptyPages = 0;
        const MAX_CONSECUTIVE_EMPTY = 3; // Stop after 3 consecutive empty pages
        
        console.log(`🔧 Tools: Starting application loading...`);
        
        do {
          pageCount++;
          console.log(`🔧 Tools: Fetching applications page ${pageCount}...`);
          
          let request = client.api("/applications")
            .select("id,appId,displayName,web,spa,api,appRoles,passwordCredentials,tags,identifierUris,appOwnerTenantId,signInAudience,publisherDomain,requiredResourceAccess")
            .top(100); // Maximum page size
          
          if (nextLink) {
            try {
              const url = new URL(nextLink);
              const skipToken = url.searchParams.get('$skiptoken');
              if (skipToken) {
                request = request.skipToken(skipToken);
              } else {
                console.warn(`🔧 Tools: NextLink without skipToken: ${nextLink}`);
                // Try using the full URL path
                const fullUrl = nextLink.replace('https://graph.microsoft.com/v1.0', '');
                request = client.api(fullUrl);
              }
            } catch (urlError) {
              console.error(`🔧 Tools: Error parsing nextLink: ${nextLink}`, urlError);
              break;
            }
          }
          
          const response = await request.get();
          
          // Check if we got results
          if (response.value && response.value.length > 0) {
            allApplications.push(...response.value);
            console.log(`🔧 Tools: Page ${pageCount}: Added ${response.value.length} applications (Total: ${allApplications.length})`);
            consecutiveEmptyPages = 0; // Reset counter
          } else {
            consecutiveEmptyPages++;
            console.warn(`🔧 Tools: Page ${pageCount}: No applications returned (${consecutiveEmptyPages}/${MAX_CONSECUTIVE_EMPTY})`);
            
            // If we get too many consecutive empty pages, something might be wrong
            if (consecutiveEmptyPages >= MAX_CONSECUTIVE_EMPTY) {
              console.warn(`🔧 Tools: Too many consecutive empty pages, stopping pagination`);
              break;
            }
          }
          
          // Update nextLink for next iteration
          nextLink = response['@odata.nextLink'];
          
          // Safety check to prevent infinite loops
          if (pageCount >= MAX_PAGES) {
            console.warn(`🔧 Tools: Reached maximum page limit (${MAX_PAGES}). Stopping pagination.`);
            break;
          }
          
        } while (nextLink);
        
        console.log(`✅ Tools: Application loading complete: ${allApplications.length} total applications loaded in ${pageCount} pages`);
        
        // Production logging without hardcoded expectations
        if (pageCount >= MAX_PAGES) {
          console.warn(`⚠️ Tools: Pagination stopped at safety limit. There may be more applications.`);
        }
        
        if (allApplications.length === 0) {
          console.info(`ℹ️ Tools: No applications found. This may be normal for new tenants.`);
        } else if (allApplications.length > 1000) {
          console.info(`ℹ️ Tools: Large number of applications (${allApplications.length}). Consider implementing lazy loading.`);
        }
        
        // Update state with all applications
        setApplications(allApplications);
        setApplicationsLoaded(true);
        
        // After loading applications, get service principals in background
        loadServicePrincipalsOptimized(allApplications);
      } catch (err) {
        console.error("🔧 Tools: Error loading applications:", err);
      } finally {
        setLoading(false);
      }
    };
    
    loadApplicationsOptimized();
  }, [instance, accounts, applicationsLoaded, tokenService]);

  // OPTIMIZED: Background service principal loading with better performance
  const loadServicePrincipalsOptimized = useCallback(async (apps: Application[]) => {
    if (!apps.length || !accounts.length) return;
    
    try {
      // Use read-only scopes; SP listing may require Directory.Read.All
      let accessToken: string;
      const spScopes = ["Application.Read.All", "Directory.Read.All"];
      try {
        accessToken = await tokenService.getToken(spScopes, accounts[0]);
      } catch (e: any) {
        if (e instanceof InteractionRequiredAuthError || e?.errorCode === 'consent_required') {
          console.log("🔧 Tools: Consent required for SP read scopes, invoking popup...", spScopes);
          const resp = await instance.acquireTokenPopup({
            scopes: spScopes,
            account: accounts[0]
          });
          accessToken = resp.accessToken;
        } else {
          throw e;
        }
      }
      
      const client = Client.init({ authProvider: (done) => done(null, accessToken) });
      const spMap: Record<string, string> = {};
      
      // OPTIMIZED: Process in smaller batches to prevent overwhelming the API
      const BATCH_SIZE = 20; // Reduced batch size for better reliability
      const MAX_CONCURRENT_BATCHES = 2; // Limit concurrent requests
      
      for (let i = 0; i < apps.length; i += BATCH_SIZE) {
        const batch = apps.slice(i, i + BATCH_SIZE);
        
        // Process batches with limited concurrency
        const batchPromises = batch
          .filter(app => app.appId) // Only process apps with appId
          .map(async (app) => {
            try {
              const spRes = await client
                .api(`/servicePrincipals?$filter=appId eq '${app.appId}'&$select=id,preferredSingleSignOnMode`)
                .get();
                
              if (spRes.value?.length) {
                const sp = spRes.value[0];
                spMap[app.appId!] = sp.preferredSingleSignOnMode || '';
              }
            } catch (err) {
              // Silently continue on errors to avoid breaking the entire process
              console.warn(`🔧 Tools: Failed to get SP for app ${app.displayName}:`, err);
            }
          });
        
        // Wait for current batch to complete
        await Promise.all(batchPromises);
        
        // Update state progressively
        setServiceProviders(prevMap => ({ ...prevMap, ...spMap }));
        
        // Small delay to prevent rate limiting
        if (i + BATCH_SIZE < apps.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log(`✅ Tools: Service principals loaded for ${Object.keys(spMap).length} applications`);
    } catch (err) {
      console.error("🔧 Tools: Failed to load service principals:", err);
    }
  }, [accounts, tokenService]);

  // Helper function to determine if an app is SAML-based
  const isSamlApp = (app: Application): boolean => {
    if (!app.appId) return false;
    return serviceProviders[app.appId] === 'saml';
  };

  // Define available tools (including SchemaExtensionsTool)
  const tools: Tool[] = [
    { 
      id: 'app-testing', 
      name: 'Auth Flow Tester', 
      description: 'Test applications with OAuth or SAML flows based on app type', 
      icon: <AppIcon sx={{ color: '#2196f3' }} />, // Blue
    },
    {
      id: 'msal-trace-viewer',
      name: 'MSAL Trace Viewer',
      description: 'Live view of msal-browser events grouped by correlationId',
      icon: <TimelineIcon sx={{ color: '#1565c0' }} />, // Blue-ish
      isNew: true,
    },
    {
      id: 'oidc-metadata-inspector',
      name: 'OIDC Metadata & JWKS Inspector',
      description: 'Inspect OIDC discovery, JWKS keys, and verify JWT signatures',
      icon: <InfoOutlinedIcon sx={{ color: '#0277bd' }} />, // Teal/blue
      isNew: true,
    },
    { 
      id: 'jwt-decoder', 
      name: 'JWT Token Decoder', 
      description: 'Decode and analyze JWT tokens', 
      icon: <VpnKeyIcon sx={{ color: '#4caf50' }} />, // Green
      component: TokenDecoder
    },
    { 
      id: 'graph-explorer', 
      name: 'Graph Explorer', 
      description: 'Test Microsoft Graph API endpoints', 
      icon: <ExploreIcon sx={{ color: '#ff9800' }} />, // Orange
      component: GraphExplorer,
    },
    {
      id: 'odata-builder',
      name: 'OData Query Builder',
      description: 'Build $select, $filter, $orderby, $top, $count visually and run queries',
      icon: <CodeIcon sx={{ color: '#fb8c00' }} />, // Orange-ish
      component: ODataQueryBuilder,
      requiredPermissions: ['Application.Read.All']
    },
    {
      id: 'entra-endpoints-explorer',
      name: 'Entra ID Endpoints Explorer',
      description: 'Comprehensive directory of Entra ID, Graph API, B2C, and Azure service endpoints',
      icon: <ApiIcon sx={{ color: '#0288d1' }} />,
      component: EntraEndpointsExplorer,
    },
    { 
      id: 'manifest-viewer', 
      name: 'Application Manifest Viewer', 
      description: 'View application manifests in read-only mode', 
      icon: <BuildOutlinedIcon sx={{ color: '#9c27b0' }} />, // Purple
      component: ManifestViewer,
      // Show key permission for quick glance in card footer
      requiredPermissions: ["Application.Read.All"]
    },
    {
      id: 'token-scope-requester',
      name: 'Token Scope Requester',
      description: 'Request delegated tokens for arbitrary scopes and inspect claims',
      icon: <LockOpenIcon sx={{ color: '#2e7d32' }} />, // Greenish
    },
    {
      id: 'raw-oauth-tester',
      name: 'Raw OAuth (no MSAL)',
      description: 'Run Implicit and Hybrid authorize requests; validate state/nonce and tokens',
      icon: <LockOpenIcon sx={{ color: '#1565c0' }} />,
      isNew: true,
    },
    {
      id: 'confidential-client-auth-tester',
      name: 'Confidential Client Auth Tester',
      description: 'Test confidential client (web app) OAuth flows and inspect tokens',
      icon: <SecurityIcon sx={{ color: '#6366f1' }} />,
    },
    {
      id: 'token-comparison',
      name: 'Token Comparison Tool',
      description: 'Compare two JWT tokens side-by-side to identify claim differences (ID vs Access, v1 vs v2, etc.)',
      icon: <CompareArrowsIcon sx={{ color: '#7c3aed' }} />,
    },
    {
      id: 'msal-cache-inspector',
      name: 'MSAL Cache Inspector',
      description: 'Inspect browser storage, view cached tokens and accounts, simulate acquireTokenSilent behavior',
      icon: <StorageIcon sx={{ color: '#0277bd' }} />,
    },
    {
      id: 'msal-error-decoder',
      name: 'MSAL Error Decoder',
      description: 'Decode and troubleshoot MSAL authentication error codes with remediation guidance',
      icon: <BugReportIcon sx={{ color: '#d32f2f' }} />,
    },
    {
      id: 'schema-extensions',
      name: 'Claims Extensions Reference',
      description: 'View directory extensions and attributes for use in claims mapping policies',
      icon: <SchemaIcon sx={{ color: '#3f51b5' }} />,
      component: SchemaExtensionsTool,
      requiredPermissions: ['Directory.Read.All']
    }
  ];

  // Define tool categories for visual organization
  const toolCategories = {
    // Keep auth-focused tools together
    "Authentication": ['app-testing', 'token-scope-requester', 'raw-oauth-tester', 'confidential-client-auth-tester', 'jwt-decoder', 'token-comparison', 'msal-cache-inspector', 'msal-trace-viewer'],
    // Debugging and inspection helpers
    "Helpers & Debugging": ['msal-error-decoder', 'oidc-metadata-inspector', 'entra-endpoints-explorer', 'schema-extensions'],
    // General purpose Graph testing utilities
    "Graph & API Exploration": ['graph-explorer', 'odata-builder'],
    // Application-centric management actions
    "Application Management": ['manifest-viewer']
  } as const;

  // Filter tools based on search query
  const filteredTools = searchQuery
    ? tools.filter(tool => 
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tools;

  // Enhanced visual styling for categories with consistent dark indigo theme
  const getCategoryStyles = (category: string) => {
    // Keep a consistent, professional dark indigo header theme across categories
    const base = {
      color: '#312e81',
      borderColor: '#4c1d95',
      bgColor: '#e0e7ff'
    };
    const styles: Record<string, typeof base> = {
      'Authentication': base,
      'Application Management': base,
      'Graph & API Exploration': base,
      'Helpers & Debugging': {
        color: '#115e59',
        borderColor: '#0f766e',
        bgColor: '#ccfbf1'
      },
    };
    return styles[category] || base;
  };

  // Enhanced tool card styling
  const getToolCardStyles = (toolId: string) => {
    const toolStyles = {
      'app-testing': { iconBg: '#eff6ff', iconColor: '#2563eb' },
      'jwt-decoder': { iconBg: '#f0fdf4', iconColor: '#059669' },
      'graph-explorer': { iconBg: '#fff7ed', iconColor: '#ea580c' },
      'odata-builder': { iconBg: '#fff7ed', iconColor: '#fb8c00' },
  'entra-endpoints-explorer': { iconBg: '#e3f2fd', iconColor: '#0288d1' },
      'manifest-viewer': { iconBg: '#faf5ff', iconColor: '#9333ea' },
      'raw-oauth-tester': { iconBg: '#e3f2fd', iconColor: '#1565c0' },
      'confidential-client-auth-tester': { iconBg: '#eef2ff', iconColor: '#6366f1' },
      'token-comparison': { iconBg: '#f5f3ff', iconColor: '#7c3aed' },
      'msal-cache-inspector': { iconBg: '#e1f5fe', iconColor: '#0277bd' },
      'msal-error-decoder': { iconBg: '#ffebee', iconColor: '#d32f2f' },
      'schema-extensions': { iconBg: '#e8eaf6', iconColor: '#3f51b5' },
    };
    return toolStyles[toolId as keyof typeof toolStyles] || { iconBg: '#f8fafc', iconColor: '#64748b' };
  };

  // Enhanced tool selection handler with comprehensive token management
  const handleToolSelect = async (toolId: string) => {
    console.log(`🔧 Tools: Tool selected: ${toolId}`);

    // Notify parent that a tool is now active
    if (onToolStateChange) {
      onToolStateChange(true);
    }
    
    // Allow tools that don't require MSAL sign-in
    if (toolId === 'raw-oauth-tester' || toolId === 'msal-trace-viewer' || toolId === 'oidc-metadata-inspector' || toolId === 'entra-endpoints-explorer' || toolId === 'token-comparison' || toolId === 'msal-cache-inspector' || toolId === 'msal-error-decoder') {
      sessionStorage.setItem('toolActivationTime', new Date().toISOString());
      setActiveTool(toolId);
      setAuthInProgress(false);
      setAuthTool(null);
      return;
    }

    if (!accounts || accounts.length === 0) {
      alert("Please sign in first to access tools.");
      return;
    }

    // Check if token service is available
    if (!tokenService) {
      console.error("🔧 Tools: Token service not available");
      alert("Authentication service is not available. Please refresh the page.");
      return;
    }

  // Show authentication in progress for this tool
  setAuthInProgress(true);
  setAuthTool(toolId);

    const currentAccount = accounts[0];
    const selectedToolInfo = tools.find(tool => tool.id === toolId);
    
    // Determine required scopes based on tool
    let requiredScopes: string[] = ["User.Read"]; // Default scope
    
    try {
      // Clear any previous tool state
      sessionStorage.removeItem('lastActiveTool');
      
      switch(toolId) {
        case 'graph-explorer':
          requiredScopes = [
            "User.Read"
          ];
          break;
        case 'manifest-viewer':
          requiredScopes = ["Application.Read.All"];
          break;
        case 'schema-extensions':
          requiredScopes = ["Directory.Read.All"];
          break;
        case 'token-scope-requester':
          // Activate immediately; component will handle scope prompts
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          setActiveTool(toolId);
          setAuthInProgress(false);
          setAuthTool(null);
          return;
        default:
          // Use default scope or tool-specific scopes if defined
          requiredScopes = selectedToolInfo?.requiredPermissions || ["User.Read"];
      }
      
      console.log(`🔧 Tools: Acquiring token for ${toolId} with scopes:`, requiredScopes);
      
      // For GraphExplorer specifically, we use an enhanced UX approach
      if (toolId === 'graph-explorer') {
        try {
          // UPDATED: Try to use the existing token first without requesting additional scopes
          console.log("🔧 Tools: Attempting to use existing token for GraphExplorer...");
          
          // Try to get existing token silently with minimal scopes
          const accessToken = await tokenService.getToken(
            requiredScopes, 
            currentAccount
          );
          
          // If successful, proceed without popup
          setAccessToken(accessToken);
          console.log(`🔧 Tools: Using existing token for ${toolId}`);
          
          // Record activation time for navigation detection
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          
          // Directly activate the selected tool
          setActiveTool(toolId);
        } catch (error: any) {
          console.log("🔧 Tools: Existing token insufficient, will request permissions as needed:", error);
          
          // UPDATED: Don't immediately request additional scopes, let GraphExplorer handle it
          // Just proceed with whatever token we can get
          try {
            const response = await instance.acquireTokenSilent({
              scopes: ["User.Read"], // Minimal scope
              account: currentAccount
            });
            
            setAccessToken(response.accessToken);
            console.log("🔧 Tools: Using minimal token, GraphExplorer will request additional permissions as needed");
            
            // Record activation time for navigation detection
            sessionStorage.setItem('toolActivationTime', new Date().toISOString());
            
            // Directly activate the selected tool
            setActiveTool(toolId);
          } catch (silentError: any) {
            // Only if we can't get even a basic token, then request interactive auth
            console.log("🔧 Tools: No existing token available, requesting interactive auth");
            
            const response = await instance.acquireTokenPopup({
              scopes: ["User.Read"],
              account: currentAccount
            });
            
            setAccessToken(response.accessToken);
            console.log("🔧 Tools: Interactive authentication successful");
            
            // Record activation time for navigation detection
            sessionStorage.setItem('toolActivationTime', new Date().toISOString());
            
            // Directly activate the selected tool
            setActiveTool(toolId);
          }
        }
      } else {
        // Standard flow for other tools using TokenService
        try {
          // Use the token service to get token with required scopes
          const accessToken = await tokenService.getToken(
            requiredScopes, 
            currentAccount
          );
          
          // Store the acquired token
          setAccessToken(accessToken);
          console.log(`🔧 Tools: Token successfully acquired for ${toolId}`);
          
          // Record activation time for navigation detection
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          
          // Tool-specific handling
          if (toolId === 'app-testing') {
            setShowAppTesting(true);
            setActiveTool(null);
          } else {
            // Directly activate the selected tool
            setActiveTool(toolId);
          }
        } catch (error: any) {
          // Re-throw to be caught by outer catch
          throw error;
        }
      }
    } catch (error: any) {
      console.error(`🔧 Tools: Error acquiring token for ${toolId}:`, error);
      
      // Enhanced error categorization and handling
      if (error.name === "InteractionRequiredAuthError" || 
          error.name === "BrowserAuthError" ||
          error.errorCode === "consent_required") {
        
        console.log("🔧 Tools: Interactive authentication required, attempting popup flow");
        
        try {
          // Try interactive authentication via popup
          const popupScopes = selectedToolInfo?.requiredPermissions || requiredScopes;
          console.log("🔧 Tools: Requesting consent via popup for scopes:", popupScopes);
          
          const response = await instance.acquireTokenPopup({
            scopes: popupScopes,
            account: currentAccount
          });
          
          setAccessToken(response.accessToken);
          console.log("🔧 Tools: Interactive authentication successful");
          
          // Record activation time for navigation detection
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          
          // Tool-specific handling after interactive auth
          if (toolId === 'app-testing') {
            setShowAppTesting(true);
            setActiveTool(null);
          } else {
            // Directly activate the tool
            setActiveTool(toolId);
          }
        } catch (popupError: any) {
          console.error("🔧 Tools: Interactive authentication failed:", popupError);
          
          // Detailed popup error handling
          if (popupError.name === "BrowserAuthError" && 
              popupError.message.includes("popup")) {
            alert("Popup blocked. Please enable popups for this site and try again.");
          } else if (popupError.name === "UserCancelledError" || 
                    popupError.message.includes("cancelled")) {
            console.log("🔧 Tools: User cancelled the authentication process");
            // No alert needed as this was user's choice
          } else {
            alert(`Unable to acquire necessary permissions for ${selectedToolInfo?.name || toolId}. Please try again.`);
          }
        }
      } else if (error.name === "ClientAuthError" || 
                error.errorCode === "client_error") {
        alert(`Authentication configuration error. Please try again later or contact support.`);
      } else {
        // Generic error handling
        alert(`Failed to access ${selectedToolInfo?.name || toolId}. ${error.message || "Please try again."}`);
      }
    } finally {
      // Always clear the authentication progress state
      setAuthInProgress(false);
      setAuthTool(null);
    }
  };
  
  // Handle app selection for testing - dynamically determine SAML or OAuth flow
  const handleAppSelected = (app: Application) => {
    setSelectedApp(app);
    setShowAppTesting(false);
    
    // Determine which tester to use based on app type
    if (isSamlApp(app)) {
      setActiveTool('saml');
    } else {
      setActiveTool('auth-flow');
    }
  };

  // Note: Manifest editing has been removed. Viewer is read-only.

  // *** UPDATED handleBack function ***
  const handleBack = () => {
    // Use browser history so we pop to the prior state instead of creating a new one
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    // Fallback (very first entry or no history): manually reset
    setActiveTool(null);
    setSelectedApp(null);
    setShowAppTesting(false);
    if (onToolStateChange) onToolStateChange(false);
  };

  // Function for handling token decoding in tools
  const handleDecodedToken = (token: any) => {
    // console.log("Decoded token:", token); // disabled for security
    // Implement any callback handling needed
  };

  // UPDATE overview onBack callbacks to also use history.back()

  if (showAppTesting) {
    return (
      <AppOverview
        apps={applications}
        onBack={() => {
          if (window.history.length > 1) {
            window.history.back();
            return;
          }
          if (onToolStateChange) onToolStateChange(false);
          setShowAppTesting(false);
          setActiveTool(null);
          setSelectedApp(null);
        }}
        onSelectAppForTesting={handleAppSelected}
      />
    );
  }

  // Type-safe rendering of active tool components
  const renderActiveTool = () => {
    if (!activeTool) return null;
    
    // Special handling for SAML and Auth Flow tools which aren't in the tools array
    if (activeTool === 'saml' && selectedApp) {
      return <SamlTester 
        app={selectedApp as any}
        onBack={handleBack} 
        msalInstance={instance}
      />;
    }
    
    if (activeTool === 'auth-flow' && selectedApp) {
      return <AuthFlowTester 
        app={selectedApp as any}
        onBack={handleBack} 
        onDecodedTokenTested={handleDecodedToken} 
      />;
    }
    
    // Token Scope Requester
    if (activeTool === 'token-scope-requester') {
      return <TokenScopeRequester onBack={handleBack} tokenService={tokenService} account={accounts[0]} msalInstance={instance} />;
    }

    // Raw OAuth Tester
    if (activeTool === 'raw-oauth-tester') {
      return <RawOAuthTester onBack={handleBack} />;
    }

    // Token Comparison Tool - no auth required
    if (activeTool === 'token-comparison') {
      return <TokenComparisonTool onBack={handleBack} />;
    }

    // MSAL Cache Inspector - reads browser storage, no auth required
    if (activeTool === 'msal-cache-inspector') {
      return <MsalCacheInspector onBack={handleBack} />;
    }

    // MSAL Error Decoder - pure client-side, no auth required
    if (activeTool === 'msal-error-decoder') {
      return <MsalErrorDecoder onBack={handleBack} />;
    }

    // Confidential Client Auth Tester
    if (activeTool === 'confidential-client-auth-tester') {
      return <ConfidentialClientAuthDebugger onBack={handleBack} applications={applications} />;
    }
    
    // For other tools, find them in the tools array
    const tool = tools.find(t => t.id === activeTool);
    if (!tool?.component) {
      // Handle tools rendered without component via explicit cases
      if (activeTool === 'msal-trace-viewer') {
        return <MsalTraceViewer onBack={handleBack} />;
      }
      if (activeTool === 'oidc-metadata-inspector') {
        return <OidcMetadataInspector onBack={handleBack} />;
      }
      return (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6">
            {tool?.name || "Tool"} is not implemented yet
          </Typography>
          <Button 
            variant="outlined"
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Tools
          </Button>
        </Box>
      );
    }

    // We'll use type casting and conditional rendering instead of a switch statement
    if (activeTool === 'graph-explorer') {
      const GraphExplorerComponent = tool.component as typeof GraphExplorer;
      return <GraphExplorerComponent 
        accessToken={accessToken} 
        onBack={handleBack}
        isReadOnly={true}
      />;
    }
    
    if (activeTool === 'manifest-viewer') {
      const ManifestViewerComponent = tool.component as typeof ManifestViewer;
      return <ManifestViewerComponent 
        applications={applications}
        onBack={handleBack}
      />;
    }
    
    if (activeTool === 'odata-builder') {
      const ODataComponent = tool.component as typeof ODataQueryBuilder;
      return <ODataComponent accessToken={accessToken} onBack={handleBack} />;
    }
    
    if (activeTool === 'jwt-decoder') {
      const TokenDecoderComponent = tool.component as typeof TokenDecoder;
      return <TokenDecoderComponent 
        onBack={handleBack} 
      />;
    }

    if (activeTool === 'entra-endpoints-explorer') {
      const EntraEndpointsExplorerComponent = tool.component as typeof EntraEndpointsExplorer;
      return <EntraEndpointsExplorerComponent onBack={handleBack} />;
    }

    if (activeTool === 'schema-extensions') {
      const SchemaExtensionsComponent = tool.component as typeof SchemaExtensionsTool;
      return <SchemaExtensionsComponent onBack={handleBack} />;
    }

    // Note: msal-trace-viewer and oidc-metadata-inspector are handled above when no component is present
    
    // Default fallback
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6">
          Tool configuration error
        </Typography>
        <Button 
          variant="outlined"
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to Tools
        </Button>
      </Box>
    );
  };

  // If a tool is active, render it
  if (activeTool) {
    return (
      <>
        {renderActiveTool()}
        
        {/* Authentication in progress overlay - moved here so it works globally */}
        {authInProgress && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 3 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6">
                Authenticating for {authTool ? tools.find(t => t.id === authTool)?.name || authTool : 'tool'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                A popup window may appear briefly for consent verification
              </Typography>
            </Paper>
          </Box>
        )}
      </>
    );
  }

  // Enhanced tools list with better visual design
return (
  <Box sx={{ pb: 2 }}>
    {/* View mode toggle */}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <ToggleButtonGroup
        size="small"
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        aria-label="tools view mode"
      >
        <ToggleButton value="cards" aria-label="cards view">
          <ViewModuleIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton value="list" aria-label="list view">
          <ViewListIcon fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>

    {Object.entries(toolCategories).map(([category, toolIds], index) => {
      const categoryTools = filteredTools.filter(tool => (toolIds as readonly string[]).includes(tool.id));
      if (categoryTools.length === 0) return null;

      const categoryStyle = getCategoryStyles(category);
      const isLastCategory = index === Object.entries(toolCategories).length - 1;

      return (
        <div key={category}>
          <Box sx={{ mb: 2.5, px: 0, py: 1, borderRadius: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ fontWeight: 600, fontSize: '1.2rem', color: categoryStyle.color, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              {category}
              <Box sx={{ flex: 1, height: '1px', backgroundColor: categoryStyle.borderColor, opacity: 0.3, ml: 2 }} />
            </Typography>
          </Box>

          {viewMode === 'cards' ? (
            // Cards grid (existing UI)
            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', '@media (max-width: 768px)': { gridTemplateColumns: '1fr' } }}>
              {categoryTools.map(tool => {
                const cardStyle = getToolCardStyles(tool.id);
                return (
                  <Paper key={tool.id} elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e2e8f0', background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', borderColor: '#cbd5e1', '& .tool-icon': { transform: 'scale(1.1) rotate(5deg)' }, '& .tool-card-bg': { opacity: 1, transform: 'scale(1.05)' } } }} onClick={() => handleToolSelect(tool.id)}>
                    <Box className="tool-card-bg" sx={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: `linear-gradient(135deg, ${cardStyle.iconColor}15, ${cardStyle.iconColor}05)`, borderRadius: '50%', opacity: 0.7, transform: 'scale(0.8)', transition: 'all 0.3s ease', zIndex: 0 }} />
                    <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: cardStyle.iconBg, border: `2px solid ${cardStyle.iconColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, transition: 'all 0.3s ease', width: 56, height: 56 }} className="tool-icon">
                          {React.cloneElement(tool.icon as React.ReactElement, { sx: { fontSize: '1.5rem', color: cardStyle.iconColor, filter: `drop-shadow(0 2px 4px ${cardStyle.iconColor}30)` } })}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', lineHeight: 1.2, letterSpacing: '-0.01em' }}>{tool.name}</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.5, fontSize: '0.9rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{tool.description}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #f1f5f9' }}>
                        <Typography variant="caption" sx={{ color: cardStyle.iconColor, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{category.toLowerCase()}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8', fontSize: '0.8rem', fontWeight: 500, overflow: 'hidden' }}>
                          {Array.isArray(tool.requiredPermissions) && tool.requiredPermissions.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
                              {tool.requiredPermissions.slice(0, 2).map((perm) => (
                                <Box key={perm} component="span" title={perm} sx={{ px: 0.75, py: 0.1, borderRadius: '999px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#64748b', fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                                  {perm.replace('.ReadWrite.', '.RW.').replace('.Read.', '.R.').replace('Application.', 'App.').replace('Policy.', 'Pol.').replace('Directory.', 'Dir.')}
                                </Box>
                              ))}
                              {tool.requiredPermissions.length > 2 && (
                                <Box component="span" sx={{ px: 0.75, py: 0.1, borderRadius: '999px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#64748b', fontSize: '0.65rem', fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.4 }}>+{tool.requiredPermissions.length - 2}</Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 3, border: `2px solid ${cardStyle.iconColor}`, opacity: 0, transition: 'opacity 0.3s ease', pointerEvents: 'none', '.MuiPaper-root:hover &': { opacity: 0.3 } }} />
                  </Paper>
                );
              })}
            </Box>
          ) : (
            // List view (new)
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {categoryTools.map((tool, idx) => {
                const cardStyle = getToolCardStyles(tool.id);
                return (
                  <Paper
                    key={tool.id}
                    elevation={0}
                    sx={{
                      p: 1.25,
                      borderRadius: 0,
                      cursor: 'pointer',
                      borderLeft: 0,
                      borderRight: 0,
                      borderTop: idx === 0 ? '1px solid #e2e8f0' : 0,
                      borderBottom: '1px solid #e2e8f0',
                      '&:hover': { backgroundColor: '#f8fafc' }
                    }}
                    onClick={() => handleToolSelect(tool.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ p: 1, borderRadius: 2, backgroundColor: cardStyle.iconBg, border: `1px solid ${cardStyle.iconColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {React.cloneElement(tool.icon as React.ReactElement, { sx: { fontSize: '1.25rem', color: cardStyle.iconColor } })}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>{tool.name}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tool.description}</Typography>
                      </Box>
                      {/* No right-side metadata in list view (no permissions or category) */}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

          {!isLastCategory && (
            <Box sx={{ my: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              {[1, 2, 3, 4, 5].map(dot => (
                <Box key={dot} sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#94a3b8', opacity: dot === 3 ? 1 : 0.6 }} />
              ))}
            </Box>
          )}
        </div>
      );
    })}

    {filteredTools.length === 0 && (
      <Box sx={{ textAlign: 'center', py: 8, px: 4 }}>
        <Box sx={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
          <SearchIcon sx={{ fontSize: '2rem', color: '#94a3b8' }} />
        </Box>
        <Typography variant="h6" sx={{ mb: 1, color: '#64748b', fontWeight: 600 }}>
          No tools found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tools match your search for "<strong>{searchQuery}</strong>"
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your search terms or browse all available tools.
        </Typography>
      </Box>
    )}
  </Box>
);
};

export default ToolsContent;